import { useQuery } from '@tanstack/react-query';
import { Loader2, Search, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { partnerApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';

interface PartnerUsersTableProps {
  tenantId: string;
}

export const PartnerUsersTable: React.FC<PartnerUsersTableProps> = ({
  tenantId,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<{
    [page: number]: string | undefined;
  }>({});

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 700); // 700ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const {
    data: partnerUsersResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'tenant-partner-users',
      tenantId,
      debouncedSearchTerm,
      statusFilter,
      currentPage,
      pageCursors[currentPage], // Include cursor in query key for proper cache invalidation
    ],
    queryFn: () =>
      partnerApi.getTenantPartnerUsers(tenantId, {
        limit: 20,
        cursor: pageCursors[currentPage],
        search: debouncedSearchTerm || undefined,
        status: statusFilter || undefined,
      }),
    enabled: !!tenantId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Store cursor for next page when data changes
  useEffect(() => {
    if (partnerUsersResponse?.data?.pagination.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]:
          partnerUsersResponse.data.pagination.cursor || undefined,
      }));
    }
  }, [partnerUsersResponse, currentPage]);

  // Reset state when tenantId changes (navigation between tenants)
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [tenantId]);

  const partnerUsers = partnerUsersResponse?.data?.results || [];
  const pagination = partnerUsersResponse?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is now handled by debouncedSearchTerm
  };

  // Reset pagination when search term or status filter changes
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [debouncedSearchTerm, statusFilter]);

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <p className="text-red-700">
          Error loading users:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by name, email, or external ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>
        <select
          value={statusFilter}
          onChange={e => handleStatusFilterChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-sm text-gray-600">Loading users...</span>
        </div>
      ) : partnerUsers.length === 0 ? (
        <div className="text-center py-8">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No Users</h3>
          <p className="mt-1 text-sm text-gray-500">
            This tenant has no users yet.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    External ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {partnerUsers.map(user => (
                  <tr
                    key={user.id}
                    onClick={() =>
                      navigate(
                        `/dashboard/users/${user.externalId}?tenantId=${tenantId}`,
                      )
                    }
                    className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.firstName ||
                                user.lastName ||
                                'Unnamed User'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-mono">
                        {user.externalId}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {user.email && (
                          <div className="text-sm text-gray-900">
                            {user.email}
                          </div>
                        )}
                        {user.phone && (
                          <div className="text-sm text-gray-500">
                            {user.phone}
                          </div>
                        )}
                        {!user.email && !user.phone && (
                          <div className="text-sm text-gray-400">
                            No contact info
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${
                          user.status === 'active'
                            ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                            : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20'
                        }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {(() => {
                  const startItem = (currentPage - 1) * 20 + 1;
                  const endItem = startItem + partnerUsers.length - 1;
                  const total = pagination?.total || 0;

                  if (total === 0 && !isLoading) {
                    return 'No users found';
                  }

                  if (partnerUsers.length === 0) {
                    return 'Loading...';
                  }

                  return `Viewing ${startItem}-${endItem} of ${total} total users`;
                })()}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1 || isLoading}>
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!pagination?.hasNextPage || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Next'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
